import { record } from "./record";
import { TracingModule } from "./tracing";
import { beaconFetchPost } from "./beaconFetch";
import { resolvePasswordMask } from "./privacyDefaults";
import {
  isRecordingAllowedForPath,
  applyRecordingNav,
  effectiveRecordable,
  canCaptureDom,
} from "./recordingPaths";
import {
  buildBlockCaptureSelector,
  isElementCaptureBlocked,
} from "./elementPrivacy";
import {
  canAdoptStoredSession,
  isSessionAlive,
  parseSessionVerdict,
  shouldAdoptServerSessionId,
  shouldResetClientIpLatch,
} from "./sessionLifecycle";
import {
  TabRegistry,
  withSessionLock,
  SESSION_LOCK_TIMEOUT_MS,
  TAB_HEARTBEAT_INTERVAL_MS,
} from "./multiTab";

interface TracingConfig {
  enabled: boolean;
  sampleRate?: number; // 0.0 to 1.0, defaults to 1.0
}

interface PrivacyConfig {
  maskTextSelector?: string;
  maskAllInputs?: boolean;
  maskInputOptions?: {
    password?: boolean;
    email?: boolean;
    tel?: boolean;
    text?: boolean;
    number?: boolean;
    search?: boolean;
  };
  blockSelector?: string;
  maskTextClass?: string;
  blockClass?: string;
}

interface Breadcrumb {
  type: "click" | "navigation" | "console" | "network";
  category: string;
  message: string;
  timestamp: number;
  data?: string;
}

interface Config {
  apiKey: string;
  throttleTime?: number;
  sessionTimeout?: number;
  tracing?: boolean | TracingConfig;
  tracingOrigins?: (string | RegExp)[];
  baseUrl?: string;
  privacy?: PrivacyConfig;
}

declare const __SDK_BASE_URL__: string;
interface Metadata {
  browser: string;
  screenWidth: number;
  screenHeight: number;
  pageHeight: number;
  pagePath: string;
  language: string;
  platform: string;
}

namespace JstVerifyNamespace {
  export interface ErrorEvent {
    type: string;
    message: string;
    source?: string;
    line?: number;
    column?: number;
    stack?: string | null;
    userAgent: string;
    timestamp: number;
    breadcrumbs?: Breadcrumb[];
  }
}

interface DomSnapshotPayload {
  snapshotEvent: any;
  pagePath: string;
  screenWidth: number;
  screenHeight: number;
  timestamp: number;
  captureType: "initial" | "scroll" | "content-change";
  contentHash: string;
}

interface SendDataPayload {
  apiKey: string;
  recordingEvents: any[];
  interactions: any;
  consoleErrors?: any;
  metadata?: Metadata | Metadata[];
  sendClientIP?: boolean;
  sessionID?: string | null;
  generatedSessionID?: boolean;
  finishedSession?: boolean | number;
  domSnapshot?: DomSnapshotPayload;
  sdkVersion?: string;
  // Client clock at dispatch time. The server derives the per-session clock
  // offset from it (serverReceipt - sentAt) and anchors the session's
  // queryable timestamps to the server timeline.
  sentAt?: number;
}

interface EventPayload {
  type: string;
  [key: string]: any;
}

class JstVerifySDK {
  private static readonly MAX_EVENT_QUEUE_SIZE = 500;
  private static readonly MAX_RECORDING_QUEUE_SIZE = 1000;
  private static readonly STORAGE_FLUSH_DEBOUNCE = 2000;
  private static readonly SDK_VERSION = "1.2.1";

  private debug = false;
  private apiKey: string = "";
  private baseUrl: string = __SDK_BASE_URL__;
  private endpoint: string = `${__SDK_BASE_URL__}/v1/visitor/sessions`;
  private throttleTime = 500;
  private sessionTimeout = 5 * 60 * 1000;
  private eventQueue: EventPayload[] = [];
  private recordingEvents: any[] = [];
  private lastHoveredElement: Element | null = null;
  private hoverStartTime: number | null = null;
  private allowedPaths: string[] = [];
  private disallowedPaths: string[] = [];
  // Recording path scope. SEPARATE from allowedPaths/disallowedPaths (which
  // gate interactions/heatmaps): these gate session replay and DOM snapshots.
  // Empty/absent = record everywhere (see isRecordingAllowedForPath). Once the
  // property configures either list, the rules are strict and fail closed.
  private recordingAllowedPaths: string[] = [];
  private recordingDisallowedPaths: string[] = [];
  // Whether the CURRENT path may be recorded. Recomputed on the handshake and on
  // every SPA navigation. Defaults true so the pre-handshake early recorder can
  // buffer the initial FullSnapshot; the handshake fails it closed if the
  // landing path turns out to be excluded.
  private recordingPathAllowed = true;
  // App-controlled recording switch (pauseRecording/resumeRecording). Can only
  // make recording MORE restrictive, never less: it is AND-ed with the server's
  // recordingEnabled and the path scope, so app code cannot force recording on a
  // path or session the server excluded. Defaults true (app has not paused).
  private appRecordable = true;
  // CSS selector marking elements redacted from CAPTURE (heatmap interactions),
  // mirroring the block class/selector rrweb uses to redact replay + snapshots.
  // Recomputed when the privacy config resolves. null = nothing to exclude.
  private blockCaptureSelector: string | null = null;
  private lastEventTimestamp: number | null = null;
  private sessionID: string | null = null;
  private lastActivity = 0;
  private clientIPSent = false;
  private domainStatus?: string;
  private visitorTracking = false;
  private errorTracking = false;
  private heatmapEnabled = false;
  private recordingEnabled = false;
  private tracingEnabled = false;
  private visitorSampleRate = 1.0;
  private metadata!: Metadata;
  private stopfn: (() => void) | undefined = undefined;
  private sendingData = false;
  private privacyConfig: PrivacyConfig = {};
  private serverPrivacyConfig: PrivacyConfig = {};
  private clientPrivacyConfig: PrivacyConfig = {};
  private sendCounter = 0;
  private generatedSessionID = false;
  private navigationTriggered = false;
  private sessionEndSent = false;
  private failureCount = 0;
  private nextRetryTime = 0;
  private permanentlyBlocked = false;
  private tracingModule: TracingModule | null = null;
  private isFunctionalTest = false;
  private functionalTestRunId: string | null = null;
  private initialized = false;
  private flushIntervalId: ReturnType<typeof setTimeout> | null = null;
  private boundClickHandler: EventListener | null = null;
  private boundScrollHandler: EventListener | null = null;
  private boundMouseMoveHandler: EventListener | null = null;
  private boundMouseOverHandler: EventListener | null = null;
  private boundErrorHandler: ((event: ErrorEvent) => void) | null = null;
  private boundRejectionHandler:
    | ((event: PromiseRejectionEvent) => void)
    | null = null;
  private boundVisibilityHandler: EventListener | null = null;
  private boundPageHideHandler: EventListener | null = null;
  private boundBeforeUnloadHandler: EventListener | null = null;
  private pendingStorageWrites: Record<string, string> = {};
  private storageFlushTimer: ReturnType<typeof setTimeout> | null = null;
  private capturedSnapshotKeys = new Set<string>();
  private pendingDomSnapshot: DomSnapshotPayload | null = null;
  private domObserver: MutationObserver | null = null;
  private domObserverTimer: ReturnType<typeof setTimeout> | null = null;
  private breadcrumbs: Breadcrumb[] = [];
  private static readonly MAX_BREADCRUMBS = 25;
  private originalConsoleError: typeof console.error | null = null;
  private originalConsoleWarn: typeof console.warn | null = null;
  private originalPushState: typeof history.pushState | null = null;
  private originalReplaceState: typeof history.replaceState | null = null;
  private boundPopStateHandler: (() => void) | null = null;
  private lastNavigationUrl: string = "";
  private originalFetch: typeof fetch | null = null;
  private lastContentHashMap = new Map<string, string>();
  private spaNavigationTimer: ReturnType<typeof setTimeout> | null = null;
  private lastFlushPagePath: string = "";
  private tabRegistry: TabRegistry | null = null;
  private tabHeartbeatId: ReturnType<typeof setInterval> | null = null;

  public async init(config: Config): Promise<void> {
    if (this.initialized) {
      this.destroy();
    }
    this.debug = false;
    localStorage.setItem("jstverify_sdk_debug", this.debug.toString());
    this.apiKey = config.apiKey;
    if (config.baseUrl) {
      this.baseUrl = config.baseUrl;
      this.endpoint = `${config.baseUrl}/v1/visitor/sessions`;
    }
    this.throttleTime = config.throttleTime || 500;
    this.sessionTimeout = config.sessionTimeout || 5 * 60 * 1000;
    this.clientPrivacyConfig = config.privacy || {};
    this.eventQueue = [];
    this.recordingEvents = [];
    this.capturedSnapshotKeys.clear();
    this.pendingDomSnapshot = null;
    this.lastContentHashMap.clear();
    this.lastFlushPagePath = "";
    this.lastHoveredElement = null;
    this.hoverStartTime = null;
    this.allowedPaths = [];
    this.disallowedPaths = [];
    this.recordingAllowedPaths = [];
    this.recordingDisallowedPaths = [];
    this.recordingPathAllowed = true;
    this.lastEventTimestamp = null;
    this.lastActivity = localStorage.getItem("jstverify_last_activity")
      ? parseInt(localStorage.getItem("jstverify_last_activity") as string, 10)
      : Date.now();
    this.clientIPSent =
      localStorage.getItem("jstverify_client_ip_sent") === "true";

    // Restore the stored session unconditionally and offer it to the server:
    // the handshake verdict (server-authoritative reuse-vs-rotate) decides
    // whether it continues, using the metadata row's lastInteractionEpoch as
    // the authoritative record. Clearing locally on a stale-looking clock
    // both fragmented sessions (H-02: a fresh session always has
    // lastSend === 0) and left the old session dangling as an orphan —
    // offering it lets the server stamp its endTime at rotation time.
    this.sessionID = localStorage.getItem("jstverify_session_id");

    // Clean up legacy localStorage key from old SDK versions
    localStorage.removeItem("jstverify_api_blocked");

    // Multi-tab coordination: keep a heartbeat roster of live tabs so the
    // session-end beacon only ever fires from the LAST live tab. The tab ID
    // lives in sessionStorage, so same-tab navigations keep one roster entry.
    this.tabRegistry = new TabRegistry(
      localStorage,
      TabRegistry.persistentTabId(sessionStorage)
    );
    this.tabRegistry.beat();
    if (this.tabHeartbeatId) clearInterval(this.tabHeartbeatId);
    this.tabHeartbeatId = setInterval(() => {
      this.tabRegistry?.beat();
    }, TAB_HEARTBEAT_INTERVAL_MS);

    // Detect functional test signal injected by Playwright Lambda
    const testSignal = (window as any).__JSTVERIFY_FUNCTIONAL_TEST__;
    if (testSignal) {
      this.isFunctionalTest = true;
      this.functionalTestRunId = testSignal.testRunId || null;
      this.log("debug", "Functional test signal detected", {
        testRunId: this.functionalTestRunId,
      });
    }

    this.log("debug", "Checking if API is blocked...");
    if (this.permanentlyBlocked) {
      this.log("warn", "API is permanently blocked due to invalid API key.");
      return;
    }
    this.log("debug", "Validating API Key and Domain...");

    // Tracing: instrument fetch IMMEDIATELY (before async validation) when the
    // client explicitly requests it, so the very first API call is captured.
    // This mirrors the early-recording pattern below. Session ID is backfilled
    // after validation returns via setSessionId().
    const tracingConfig = config.tracing;
    const clientTracingEnabled =
      tracingConfig === true ||
      (typeof tracingConfig === "object" && tracingConfig.enabled);
    if (clientTracingEnabled || this.isFunctionalTest) {
      // Use only the client-provided sampleRate here — server rate isn't known yet.
      // If the client didn't specify one, default to 1.0 so we capture early fetch calls;
      // the server rate is applied after validation (below).
      const clientSampleRate =
        typeof tracingConfig === "object" && tracingConfig.sampleRate != null
          ? Math.min(1, Math.max(0, tracingConfig.sampleRate))
          : 1.0;
      const sampled = this.isFunctionalTest || Math.random() < clientSampleRate;
      if (sampled) {
        this.tracingModule = new TracingModule(
          this.apiKey,
          this.baseUrl,
          this.debug,
          config.tracingOrigins
        );
        if (this.isFunctionalTest && this.functionalTestRunId) {
          this.tracingModule.setFunctionalTestContext(this.functionalTestRunId);
        }
        this.tracingModule.start();
      }
    }

    // Start recording IMMEDIATELY with client-only privacy config to capture
    // the initial FullSnapshot before the async validation roundtrip (~100-500ms).
    // We call record() directly instead of startRecording() because
    // recordingEnabled defaults to false until the server responds.
    this.privacyConfig = this.mergePrivacyConfigs({}, this.clientPrivacyConfig);
    const earlyStop = record({
      emit: async (event: any) => {
        if (
          this.recordingEvents.length >= JstVerifySDK.MAX_RECORDING_QUEUE_SIZE
        ) {
          this.recordingEvents.shift();
        }
        this.recordingEvents.push(event);
      },
      maskTextSelector: this.privacyConfig.maskTextSelector || null,
      maskAllInputs: this.privacyConfig.maskAllInputs ?? false,
      maskInputOptions: this.privacyConfig.maskInputOptions,
      maskTextClass: this.privacyConfig.maskTextClass || "rr-mask",
      blockClass: this.privacyConfig.blockClass || "rr-block",
      blockSelector: this.privacyConfig.blockSelector || null,
    });

    // Serialize the handshake across tabs: two tabs loading in parallel with
    // no stored session each used to handshake with sessionID: null and the
    // server minted one session PER TAB. Under the lock, the first tab mints
    // (or restores) the session and writes it to localStorage synchronously;
    // waiters re-read storage and hand the winner's ID to the server, which
    // reuses it.
    await withSessionLock(async () => {
      this.adoptStoredSessionIfAlive();
      await this.setSessionVariables();
      // Liveness stamps must be visible to waiters before the lock releases.
      // Debounced writeStorage here was the two-fresh-tabs race (#221): tab B
      // saw the ID but activity/send still 0 and refused to adopt.
      if (this.sessionID) {
        this.stampSessionLivenessNow(Date.now());
      }
    });

    // Domain validation failed — stop early recording/tracing and discard events
    if (this.domainStatus !== "active") {
      if (earlyStop) earlyStop();
      this.recordingEvents = [];
      if (this.tracingModule) {
        this.tracingModule.stop();
        this.tracingModule = null;
      }
      return;
    }

    // Visitor sampling: drop this session if it falls outside the server-defined rate
    if (!this.isFunctionalTest && Math.random() >= this.visitorSampleRate) {
      if (earlyStop) earlyStop();
      this.recordingEvents = [];
      if (this.tracingModule) {
        this.tracingModule.stop();
        this.tracingModule = null;
      }
      return;
    }

    this.log("debug", "Error tracking status:", this.errorTracking);
    this.metadata = this.collectMetadata();
    this.log("debug", "Starting event tracking...");

    this.setupBreadcrumbTracking();

    // Stop early recording and handle based on server config
    if (earlyStop) earlyStop();

    // Now that the server's recording path rules have arrived, fail closed on
    // an excluded landing path: the early recorder buffered a FullSnapshot
    // before we knew the rules, and it must be discarded if this path is not
    // recordable. This is the one point where the SDK can enforce the rules it
    // could not know at page load.
    this.recordingPathAllowed = this.isRecordingPathAllowed();

    if (
      !effectiveRecordable(
        this.recordingEnabled,
        this.recordingPathAllowed,
        this.appRecordable
      )
    ) {
      // Recording disabled, the landing path is excluded, or the app paused
      // recording before init: discard the buffered events instead of
      // transmitting them.
      this.recordingEvents = [];
    } else {
      // Restart with merged server+client privacy config (set by setSessionVariables)
      const bufferedEvents = this.recordingEvents;
      this.recordingEvents = [];
      this.stopfn = this.startRecording();
      // Prepend buffered events (they have correct timestamps from initial capture)
      this.recordingEvents = bufferedEvents.concat(this.recordingEvents);
    }

    this.startTracking();

    // Tracing: update early-started module with server-provided session ID and
    // debug flag. If tracing wasn't started early (client didn't request it)
    // but the server enables it, start now.
    // Resolve the final sample rate: client-explicit takes precedence, then server.
    const resolvedSampleRate =
      typeof tracingConfig === "object" && tracingConfig.sampleRate != null
        ? Math.min(1, Math.max(0, tracingConfig.sampleRate))
        : this.visitorSampleRate;
    if (this.tracingModule) {
      if (resolvedSampleRate === 0) {
        // Server (or client) set rate to 0 — discard the early-started module
        this.tracingModule.stop();
        this.tracingModule = null;
      } else {
        this.tracingModule.setSessionId(this.sessionID);
        this.tracingModule.setDebug(this.debug);
      }
    } else if (this.tracingEnabled || this.isFunctionalTest) {
      const sampled =
        this.isFunctionalTest || Math.random() < resolvedSampleRate;
      if (sampled) {
        this.tracingModule = new TracingModule(
          this.apiKey,
          this.baseUrl,
          this.debug,
          config.tracingOrigins
        );
        this.tracingModule.setSessionId(this.sessionID);
        if (this.isFunctionalTest && this.functionalTestRunId) {
          this.tracingModule.setFunctionalTestContext(this.functionalTestRunId);
        }
        this.tracingModule.start();
      }
    }

    if (this.errorTracking) {
      this.log("debug", "Invoking setupErrorTracking...");
      this.setupErrorTracking();
    } else {
      this.log(
        "debug",
        "Error tracking is disabled, skipping setupErrorTracking."
      );
    }

    this.initialized = true;
  }

  /**
   * Adopt a session another tab wrote to localStorage. Called under the
   * cross-tab session lock before handshaking or rotating, so a waiter picks
   * up the lock winner's session instead of minting a parallel one. Idle
   * expiry is the server's job (#222 verdict); we do not gate on local stamps.
   *
   * @returns true when a different session ID was adopted.
   */
  private adoptStoredSessionIfAlive(): boolean {
    let storedId: string | null = null;
    try {
      storedId = localStorage.getItem("jstverify_session_id");
    } catch {
      return false;
    }
    if (!canAdoptStoredSession(storedId, this.sessionID)) {
      return false;
    }
    this.sessionID = storedId;
    return true;
  }

  /** Synchronous liveness stamps for cross-tab adoption under the session lock. */
  private stampSessionLivenessNow(at: number): void {
    this.lastActivity = at;
    this.writeStorageNow("jstverify_last_activity", at.toString());
    this.writeStorageNow("jstverify_last_send", at.toString());
  }

  // Sets variables for the session including sessionID, domainStatus, allowedPaths, visitorTracking,
  // and errorTracking.
  private async setSessionVariables() {
    const validation = await this.validateApiAndDomain();
    if (!validation.valid) {
      this.log("warn", "Tracking is disabled for this domain or path.");
      return;
    }
    this.domainStatus = validation.domainStatus;
    this.allowedPaths = validation.allowedPaths ?? [];
    this.disallowedPaths = validation.disallowedPaths ?? [];
    this.recordingAllowedPaths = validation.recordingAllowedPaths ?? [];
    this.recordingDisallowedPaths = validation.recordingDisallowedPaths ?? [];
    this.visitorTracking = validation.visitorTracking ?? false;
    this.debug = validation.debugEnabled ?? false;
    this.errorTracking = validation.errorTracking ?? false;
    this.heatmapEnabled = validation.heatmapEnabled ?? false;
    this.recordingEnabled = validation.recordingEnabled ?? false;
    this.tracingEnabled = validation.tracingEnabled ?? false;
    this.visitorSampleRate = validation.sampleRate ?? 1.0;
    const previousSessionId = this.sessionID;
    // The server's verdict is final: adopt whatever session ID it returned,
    // whether reused, rotated, or newly minted. The SDK never nulls its
    // session locally in response to a handshake.
    this.sessionID = validation.sessionID || null;
    this.generatedSessionID = validation.generatedSessionID || false;
    if (
      shouldResetClientIpLatch(
        validation.sessionVerdict,
        previousSessionId,
        this.sessionID
      )
    ) {
      // The server retired the session we offered. The replacement needs its
      // own client-IP linkage; keeping the old latch suppressed visitor
      // tracking for every rotated session.
      this.clientIPSent = false;
      this.removeStorageNow("jstverify_client_ip_sent");
      this.log(
        "debug",
        "Server rotated session",
        previousSessionId,
        "->",
        this.sessionID
      );
    }
    this.serverPrivacyConfig = validation.privacyConfig || {};
    this.privacyConfig = this.mergePrivacyConfigs(
      this.serverPrivacyConfig,
      this.clientPrivacyConfig
    );
    // Same block config rrweb gets (default class "rr-block"), so the SDK's own
    // heatmap interaction capture redacts the same elements as replay/snapshot.
    this.blockCaptureSelector = buildBlockCaptureSelector(
      this.privacyConfig.blockClass || "rr-block",
      this.privacyConfig.blockSelector
    );
    this.log("debug", "Session variables set:", validation);
    if (this.sessionID) {
      // Synchronous: losing this to the 2s debounce meant a quick reload
      // couldn't restore the session and minted another one. Stamps go with
      // the ID so a lock waiter can adopt immediately (#221).
      this.writeStorageNow("jstverify_session_id", this.sessionID);
      this.stampSessionLivenessNow(Date.now());
    }
  }

  // Initializes the session recording event generators
  private startRecording(): (() => void) | undefined {
    if (!this.recordingEnabled) return undefined;
    return record({
      emit: async (event: any) => {
        if (
          this.permanentlyBlocked ||
          !effectiveRecordable(
            this.recordingEnabled,
            this.recordingPathAllowed,
            this.appRecordable
          )
        ) {
          // Stop and drop: on an excluded path (or while the app has paused) we
          // do not even buffer replay events. The recorder is normally stopped
          // on the boundary crossing; this guards the brief window before that.
          if (this.stopfn) this.stopfn();
          return;
        }
        if (
          this.recordingEvents.length >= JstVerifySDK.MAX_RECORDING_QUEUE_SIZE
        ) {
          this.recordingEvents.shift();
        }
        this.recordingEvents.push(event);
        this.lastActivity = Date.now();
        this.writeStorage(
          "jstverify_last_activity",
          this.lastActivity.toString()
        );

        if (this.recordingEvents.length + this.eventQueue.length >= 20) {
          this.sendData().catch(() => {});
        }
      },
      maskTextSelector: this.privacyConfig.maskTextSelector || null,
      maskAllInputs: this.privacyConfig.maskAllInputs ?? false,
      maskInputOptions: this.privacyConfig.maskInputOptions,
      maskTextClass: this.privacyConfig.maskTextClass || "rr-mask",
      blockClass: this.privacyConfig.blockClass || "rr-block",
      blockSelector: this.privacyConfig.blockSelector || null,
    });
  }

  private computeStructureHash(): string {
    const els = document.querySelectorAll("body *");
    const sig = Array.from(els)
      .slice(0, 500)
      .map((el) => {
        const tag = el.tagName;
        const cls =
          el.className && typeof el.className === "string"
            ? "." + el.className.trim().split(/\s+/).sort().join(".")
            : "";
        return tag + cls;
      })
      .join("|");
    let h = 0;
    for (let i = 0; i < sig.length; i++) {
      h = (h << 5) - h + sig.charCodeAt(i);
      h |= 0;
    }
    return h.toString(36);
  }

  private computeLayoutHash(): string {
    // Coarse hash of page layout — only tag names at shallow depth (max 3).
    // Ignores class names and deep content so it stays stable when content
    // loads inside existing containers (tab panels, lists, modals).
    // Used as contentHash sent to backend for versioning decisions.
    const tags: string[] = [];
    const walk = (el: Element, depth: number) => {
      if (depth > 3 || tags.length >= 200) return;
      tags.push(el.tagName);
      for (let i = 0; i < el.children.length; i++) {
        walk(el.children[i], depth + 1);
        if (tags.length >= 200) return;
      }
    };
    walk(document.body, 0);
    const sig = tags.join("|");
    let h = 0;
    for (let i = 0; i < sig.length; i++) {
      h = (h << 5) - h + sig.charCodeAt(i);
      h |= 0;
    }
    return h.toString(36);
  }

  private captureDomSnapshot(
    captureType: "initial" | "scroll" | "content-change" = "initial"
  ): void {
    // A DOM snapshot is the page serialized as text; it is gated by the RECORDING
    // path rules, not just heatmapEnabled. This is the exact leak the feature
    // closes: previously a snapshot was captured (as the heatmap background)
    // even with recording off, so excluding a path suppressed its clicks but
    // still transmitted its DOM. Reads the live path so a capture scheduled
    // across a navigation cannot slip through a stale cache. Also honors an app
    // pause (canCaptureDom), independent of recordingEnabled since the snapshot
    // doubles as the heatmap background.
    if (!canCaptureDom(this.isRecordingPathAllowed(), this.appRecordable))
      return;

    const pagePath = this.getPagePath();
    const key = `${pagePath}:${window.innerWidth}`;
    // Bypass dedup for scroll/content-change re-captures
    if (captureType === "initial" && this.capturedSnapshotKeys.has(key)) return;

    // Sensitive hash for SDK-side dedup (detects any DOM change)
    const structureHash = this.computeStructureHash();

    // For non-initial captures, skip if DOM hasn't changed at all
    if (captureType !== "initial") {
      const lastHash = this.lastContentHashMap.get(key);
      if (lastHash === structureHash) return;
    }

    // pendingDomSnapshot is a single slot; if one is already queued and this
    // new capture is for a different page/layout, flushing first prevents the
    // old snapshot from being silently overwritten (was causing missing
    // snapshots on screens where users navigated several paths between flushes).
    if (
      this.pendingDomSnapshot &&
      (this.pendingDomSnapshot.pagePath !== pagePath ||
        this.pendingDomSnapshot.screenWidth !== window.innerWidth)
    ) {
      this.sendData().catch(() => {});
    }

    // Coarse hash for backend versioning (only changes on layout shifts)
    const contentHash = this.computeLayoutHash();

    // If recording is already running, force a fresh FullSnapshot
    if (this.recordingEnabled && this.stopfn) {
      try {
        // takeFullSnapshot is synchronous — the emitted event lands in recordingEvents immediately
        record.takeFullSnapshot(true);
      } catch {
        // Recording may not be fully initialized yet; fall through to temporary recording path
      }
      // Find the LAST FullSnapshot (reverse search) — this is the fresh one we just triggered
      let fullSnapshot: any = null;
      for (let i = this.recordingEvents.length - 1; i >= 0; i--) {
        if (this.recordingEvents[i].type === 2) {
          fullSnapshot = this.recordingEvents[i];
          break;
        }
      }
      if (fullSnapshot) {
        this.pendingDomSnapshot = {
          snapshotEvent: fullSnapshot,
          pagePath,
          screenWidth: window.innerWidth,
          screenHeight: window.innerHeight,
          timestamp: Date.now(),
          captureType,
          contentHash,
        };
        this.capturedSnapshotKeys.add(key);
        this.lastContentHashMap.set(key, structureHash);
        return;
      }
    }

    // Fallback: briefly start a recording to capture the snapshot
    let stopCapture: (() => void) | undefined;
    stopCapture = record({
      emit: (event: any) => {
        if (event.type === 2) {
          // FullSnapshot
          this.pendingDomSnapshot = {
            snapshotEvent: event,
            pagePath,
            screenWidth: window.innerWidth,
            screenHeight: window.innerHeight,
            timestamp: Date.now(),
            captureType,
            contentHash,
          };
          this.capturedSnapshotKeys.add(key);
          this.lastContentHashMap.set(key, structureHash);
          stopCapture?.();
        }
      },
      maskTextSelector: this.privacyConfig.maskTextSelector || null,
      maskAllInputs: this.privacyConfig.maskAllInputs ?? false,
      maskInputOptions: this.privacyConfig.maskInputOptions,
      maskTextClass: this.privacyConfig.maskTextClass || "rr-mask",
      blockClass: this.privacyConfig.blockClass || "rr-block",
      blockSelector: this.privacyConfig.blockSelector || null,
    });
    // Safety: stop after 500ms even if no snapshot captured
    setTimeout(() => stopCapture?.(), 500);
  }

  private log(
    level: "debug" | "info" | "warn" | "error",
    message: string,
    ...args: any[]
  ): void {
    if (!this.debug && level === "debug") return;
    const prefix = `[JstVerify:${level.toUpperCase()}]`;
    switch (level) {
      case "debug":
        console.debug(prefix, message, ...args);
        break;
      case "info":
        console.info(prefix, message, ...args);
        break;
      case "warn":
        console.warn(prefix, message, ...args);
        break;
      case "error":
        console.error(prefix, message, ...args);
        break;
    }
  }

  private writeStorage(key: string, value: string): void {
    this.pendingStorageWrites[key] = value;
    if (!this.storageFlushTimer) {
      this.storageFlushTimer = setTimeout(() => {
        this.flushStorage();
      }, JstVerifySDK.STORAGE_FLUSH_DEBOUNCE);
    }
  }

  /**
   * Synchronous write for lifecycle-critical keys (session ID + liveness
   * stamps on mint/adopt/rotation). The normal sendData() success path still
   * debounces last_send. Also records the value in pendingStorageWrites so a
   * later debounced flush can't clobber it with a stale queued value.
   */
  private writeStorageNow(key: string, value: string): void {
    this.pendingStorageWrites[key] = value;
    try {
      localStorage.setItem(key, value);
    } catch {
      // Storage full/unavailable — the debounced flush will retry.
    }
  }

  private removeStorageNow(key: string): void {
    delete this.pendingStorageWrites[key];
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage unavailable — nothing to remove anyway.
    }
  }

  private flushStorage(): void {
    for (const key of Object.keys(this.pendingStorageWrites)) {
      localStorage.setItem(key, this.pendingStorageWrites[key]);
    }
    this.pendingStorageWrites = {};
    if (this.storageFlushTimer) {
      clearTimeout(this.storageFlushTimer);
      this.storageFlushTimer = null;
    }
  }

  private shouldThrottle(): boolean {
    if (this.permanentlyBlocked) return true;
    return Date.now() < this.nextRetryTime;
  }

  private recordFailure(permanent: boolean = false): void {
    if (permanent) {
      this.permanentlyBlocked = true;
      return;
    }
    this.failureCount++;
    const baseDelay = Math.min(1000 * Math.pow(2, this.failureCount), 300000);
    const jitter = baseDelay * 0.25 * Math.random();
    this.nextRetryTime = Date.now() + baseDelay + jitter;
  }

  private recordSuccess(): void {
    this.failureCount = 0;
    this.nextRetryTime = 0;
  }

  private collectMetadata(): Metadata {
    return {
      browser: navigator.userAgent,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      pageHeight: this.getPageHeight(),
      pagePath: this.getPagePath(),
      language: navigator.language,
      platform:
        (navigator as any).userAgentData?.platform ||
        navigator.platform ||
        "unknown",
    };
  }

  private getCurrentScrollDepth(): number {
    const scrollableHeight =
      document.documentElement.scrollHeight - window.innerHeight;
    if (scrollableHeight <= 0) return 0;
    return Math.max(window.scrollY / scrollableHeight, 0);
  }

  private getScrollCompletion(): number {
    const scrollHeight = document.documentElement.scrollHeight;
    if (scrollHeight <= 0) return 0;
    return (window.scrollY + window.innerHeight) / scrollHeight;
  }

  private getPageHeight(): number {
    return document.documentElement.scrollHeight;
  }

  private getPagePath(): string {
    let pagePath;
    pagePath = window.location.pathname
      .replace(/^\/$/, "root")
      .replace(/^\//, "");
    return pagePath;
  }

  private isAllowedPath(): boolean {
    const currentPath = this.getPagePath();
    if (
      this.disallowedPaths.includes(currentPath) ||
      this.disallowedPaths.includes("*")
    )
      return false;
    return (
      this.allowedPaths.includes(currentPath) || this.allowedPaths.includes("*")
    );
  }

  // Whether session replay and DOM snapshots may be captured on the CURRENT
  // path, per the property's recording path scope. Uses the raw URL pathname
  // (not getPagePath's heatmap-normalized form) so patterns read like real URLs
  // (e.g. "/account", "/orders/*"). Unconfigured => true (record everywhere).
  private isRecordingPathAllowed(): boolean {
    return isRecordingAllowedForPath(
      window.location.pathname,
      this.recordingAllowedPaths,
      this.recordingDisallowedPaths
    );
  }

  // Recompute recordingPathAllowed on an SPA navigation and stop/discard or
  // restart the recorder if we crossed the recordable/excluded boundary. A
  // buffer must never span the boundary, so entering an excluded path stops the
  // recorder and throws the in-memory buffer away; leaving one restarts the
  // recorder so rrweb emits a fresh FullSnapshot (the previous one was
  // discarded). See applyRecordingNav.
  private handleRecordingPathChange(): void {
    const nextAllowed = this.isRecordingPathAllowed();
    const { state, effect } = applyRecordingNav(
      {
        recordingEvents: this.recordingEvents,
        pendingDomSnapshot: this.pendingDomSnapshot,
        recorderRunning: !!this.stopfn,
      },
      this.recordingPathAllowed,
      nextAllowed,
      // A paused app must not restart on navigation: fold appRecordable into the
      // enabled flag so leaving an excluded path stays stopped until resume.
      this.recordingEnabled && this.appRecordable
    );
    this.recordingPathAllowed = nextAllowed;
    if (effect === "none") return;
    if (effect === "stop-and-discard") {
      if (this.stopfn) {
        this.stopfn();
        this.stopfn = undefined;
      }
      this.recordingEvents = state.recordingEvents as any[];
      this.pendingDomSnapshot = state.pendingDomSnapshot as any;
    } else if (effect === "restart") {
      this.recordingEvents = state.recordingEvents as any[];
      if (this.stopfn) this.stopfn();
      this.stopfn = this.startRecording();
    }
  }

  // Runtime, app-controlled pause of session replay and DOM snapshots. Use it
  // to redact whole pages/states the server path rules cannot express (a PII
  // modal, a role, a consent choice). It only ever RESTRICTS: it cannot make the
  // SDK record where the server disabled recording or the path scope excludes
  // it. Interaction/heatmap-coordinate capture, tracing, error and visitor
  // tracking are unaffected. Safe to call before init(): the flag is honored
  // when the handshake completes.
  public pauseRecording(): void {
    if (!this.appRecordable) return;
    this.appRecordable = false;
    // Privacy intent: stop and discard, exactly like entering an excluded path,
    // so the buffer does not span the pause.
    if (this.stopfn) {
      this.stopfn();
      this.stopfn = undefined;
    }
    this.recordingEvents = [];
    this.pendingDomSnapshot = null;
    this.log("debug", "Recording paused by app");
  }

  public resumeRecording(): void {
    if (this.appRecordable) return;
    this.appRecordable = true;
    // Restart only if the SERVER still permits recording here. This is the
    // precedence clamp: app code can lift its OWN pause but never override a
    // server-disabled property or an excluded path.
    if (
      this.initialized &&
      effectiveRecordable(
        this.recordingEnabled,
        this.isRecordingPathAllowed(),
        this.appRecordable
      )
    ) {
      this.recordingPathAllowed = this.isRecordingPathAllowed();
      if (this.stopfn) this.stopfn();
      this.stopfn = this.startRecording();
    }
    this.log("debug", "Recording resumed by app");
  }

  public setPageRecordable(recordable: boolean): void {
    if (recordable) {
      this.resumeRecording();
    } else {
      this.pauseRecording();
    }
  }

  public toggleDebug(): void {
    this.debug = !this.debug;
    this.log(
      "info",
      this.debug ? "Debug mode enabled." : "Debug mode disabled."
    );
    if (this.tracingModule) {
      this.tracingModule.setDebug(this.debug);
    }
  }

  private setupErrorTracking(): void {
    this.log("debug", "Setting up error tracking...");
    this.boundErrorHandler = (event: ErrorEvent) => {
      this.log("debug", "Capturing window error:", event);
      this.queueErrorEvent({
        type: "error",
        message: event.message || "Script error",
        source: event.filename || "Unknown source",
        line: event.lineno || 0,
        column: event.colno || 0,
        stack: event.error ? event.error.stack : null,
        userAgent: navigator.userAgent,
        timestamp: Date.now(),
      });
    };
    window.addEventListener(
      "error",
      this.boundErrorHandler as EventListener,
      true
    );
    this.boundRejectionHandler = (event: PromiseRejectionEvent) => {
      this.log("debug", "Capturing unhandled promise rejection:", event);
      let message = "Unhandled promise rejection";
      let stack: string | null = null;
      let source: string | undefined;
      let line: number | undefined;
      let column: number | undefined;
      const reason = event.reason;
      if (typeof reason === "string") {
        message = reason;
      } else if (reason instanceof Error) {
        message = reason.message;
        stack = reason.stack ?? null;
        const match = stack
          ?.split("\n")[1]
          ?.match(/(?:at\s.*)?(.+):(\d+):(\d+)?$/);
        if (match) {
          source = match[1];
          line = parseInt(match[2], 10);
          column = parseInt(match[3], 10);
        }
      } else if (typeof reason === "object" && reason !== null) {
        message = JSON.stringify(reason);
      } else if (reason !== undefined) {
        message = String(reason);
      }
      this.queueErrorEvent({
        type: "unhandledrejection",
        message,
        stack,
        source,
        line,
        column,
        userAgent: navigator.userAgent,
        timestamp: Date.now(),
      });
    };
    window.addEventListener(
      "unhandledrejection",
      this.boundRejectionHandler as EventListener
    );
    try {
      if (this.debug) {
        throw new Error("Test manual error capture");
      }
    } catch (err: any) {
      this.log("debug", "Manually capturing a test error.");
      this.queueErrorEvent({
        type: "error",
        message: err.message,
        stack: err.stack,
        userAgent: navigator.userAgent,
        timestamp: Date.now(),
      });
    }
    this.log("debug", "setupErrorTracking is now active.");
  }

  private startTracking(): void {
    if (this.heatmapEnabled) {
      this.boundClickHandler = this.throttle(
        this.trackClick.bind(this),
        this.throttleTime
      );
      this.boundScrollHandler = this.throttle(
        this.trackScroll.bind(this),
        this.throttleTime
      );
      this.boundMouseMoveHandler = this.throttle(
        this.trackMouseMove.bind(this),
        this.throttleTime
      );
      this.boundMouseOverHandler = ((e: Event) =>
        this.trackHover(e as MouseEvent)) as EventListener;

      window.addEventListener("click", this.boundClickHandler!);
      window.addEventListener("scroll", this.boundScrollHandler!);
      window.addEventListener("mousemove", this.boundMouseMoveHandler!);
      document.addEventListener("mouseover", this.boundMouseOverHandler!);

      // Capture DOM snapshot for heatmap background rendering
      // Delay slightly to let rrweb recording emit the FullSnapshot first
      setTimeout(() => this.captureDomSnapshot(), 200);
      this.lastFlushPagePath = this.getPagePath();

      // Watch for DOM structural changes that add content (lazy load, AJAX, infinite scroll,
      // tab panels, modals, carousels). Re-capture on height growth >200px (fast path) or
      // content hash change (catches fixed-height container swaps). Debounced by 2s.
      let lastKnownHeight = document.documentElement.scrollHeight;
      this.domObserver = new MutationObserver(() => {
        const currentHeight = document.documentElement.scrollHeight;
        const heightGrew = currentHeight - lastKnownHeight > 200;
        if (!heightGrew) {
          // No significant height change — check content hash for in-place DOM swaps
          const pagePath = this.getPagePath();
          const key = `${pagePath}:${window.innerWidth}`;
          const contentHash = this.computeStructureHash();
          const lastHash = this.lastContentHashMap.get(key);
          if (lastHash === contentHash) return;
        }
        // Debounce to let DOM settle
        if (this.domObserverTimer) clearTimeout(this.domObserverTimer);
        this.domObserverTimer = setTimeout(() => {
          lastKnownHeight = document.documentElement.scrollHeight;
          this.captureDomSnapshot("content-change");
          this.domObserverTimer = null;
        }, 2000);
      });
      this.domObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    // Periodically send data to the backend (always needed for errors/recording)
    this.scheduleFlush();

    // Flush data on page lifecycle events to prevent data loss
    this.boundVisibilityHandler = () => {
      if (document.visibilityState === "hidden") {
        this.flush();
        // Pause recording when tab is hidden to avoid capturing dead time
        if (this.stopfn) {
          this.stopfn();
          this.stopfn = undefined;
        }
      } else if (document.visibilityState === "visible") {
        // Resume recording when tab becomes visible
        if (!this.stopfn && this.recordingEnabled) {
          this.stopfn = this.startRecording();
        }
        // Re-assert roster membership immediately: heartbeats were throttled
        // while hidden and beforeunload/pagehide may have unregistered us on
        // a bfcache traversal.
        this.tabRegistry?.beat();
        // Refresh activity so we don't immediately timeout
        this.lastActivity = Date.now();
        this.writeStorage(
          "jstverify_last_activity",
          this.lastActivity.toString()
        );
      }
    };
    this.boundPageHideHandler = () => {
      // Leave the roster BEFORE the last-tab check so a genuinely final tab
      // sees an empty roster and finishes the session. A same-tab navigation
      // re-registers under the same sessionStorage tab ID on the next load.
      this.tabRegistry?.unregister();
      this.onLeftSession();
      this.flush();
    };
    this.boundBeforeUnloadHandler = () => {
      this.tabRegistry?.unregister();
      this.onLeftSession();
      this.flush();
    };

    document.addEventListener("visibilitychange", this.boundVisibilityHandler);
    window.addEventListener("pagehide", this.boundPageHideHandler);
    window.addEventListener("beforeunload", this.boundBeforeUnloadHandler);
  }

  private scheduleFlush(): void {
    const jitter = Math.random() * 1000;
    this.flushIntervalId = setTimeout(() => {
      // Skip work when tab is hidden — recording is paused, no new data to send
      if (document.visibilityState !== "hidden") {
        // Re-capture DOM snapshot only when page path actually changed (SPA navigation)
        if (this.heatmapEnabled) {
          const currentPath = this.getPagePath();
          if (currentPath !== this.lastFlushPagePath) {
            this.lastFlushPagePath = currentPath;
            this.captureDomSnapshot("content-change");
          }
        }
        this.sendData().catch(() => {});
      }
      this.scheduleFlush();
    }, 5000 + jitter);
  }

  private beaconFetch(body: string): boolean {
    return beaconFetchPost(this.endpoint, this.apiKey, body);
  }

  private onLeftSession(): void {
    if (
      this.sessionEndSent ||
      this.permanentlyBlocked ||
      this.domainStatus !== "active" ||
      this.shouldThrottle()
    )
      return;
    // No session yet (handshake still in flight): a finish beacon with
    // sessionID: null makes the server mint a brand-new session just to end
    // it — an instant orphan. There is nothing to finish; skip.
    if (!this.sessionID) return;
    // Another tab is still feeding this session: closing THIS tab must not
    // stamp endTime on it. The last live tab (per the heartbeat roster)
    // sends the finish beacon. Best-effort only — concurrent unregister can
    // leave no beacon; the server idle sweeper (#196) guarantees closure.
    if (this.tabRegistry && !this.tabRegistry.isLastLiveTab()) return;
    if (!this.navigationTriggered) {
      const payload: Partial<SendDataPayload> = {
        apiKey: this.apiKey,
        finishedSession: new Date().getTime(),
        sessionID: this.sessionID,
        sentAt: Date.now(),
      };

      const data = JSON.stringify(payload);
      const sent = this.beaconFetch(data);
      if (sent) {
        this.sessionEndSent = true;
      } else {
        this.log(
          "warn",
          "beaconFetch failed for session end — will retry on next page lifecycle event"
        );
      }
    }
  }

  private queueErrorEvent(errorEvent: JstVerifyNamespace.ErrorEvent): void {
    if (this.permanentlyBlocked) return;
    this.log("warn", "Captured error:", errorEvent);
    this.eventQueue.push({
      ...errorEvent,
      breadcrumbs: [...this.breadcrumbs],
      pageURL: window.location.href,
      referrer: document.referrer,
      language: navigator.language,
    });
    if (
      !this.sessionID ||
      this.eventQueue.length + this.recordingEvents.length >= 20
    ) {
      this.sendData().catch(() => {});
    }
  }

  private throttle(
    func: (...args: any[]) => void,
    limit: number
  ): EventListener {
    let lastFunc: ReturnType<typeof setTimeout> | undefined;
    let lastRan = 0;
    return function (this: any, ...args: any[]) {
      const context = this;
      if (lastRan === 0) {
        func.apply(context, args);
        lastRan = Date.now();
      } else {
        clearTimeout(lastFunc);
        lastFunc = setTimeout(function () {
          if (Date.now() - lastRan >= limit) {
            func.apply(context, args);
            lastRan = Date.now();
          }
        }, limit - (Date.now() - lastRan));
      }
    };
  }

  private trackHover(event: MouseEvent): void {
    if (this.permanentlyBlocked || !this.isAllowedPath()) return;
    // Redacted elements (rr-block class / block selector) are excluded from
    // heatmap capture, matching how rrweb redacts them from replay/snapshot.
    if (
      isElementCaptureBlocked(
        event.target as Element | null,
        this.blockCaptureSelector
      )
    )
      return;
    this.lastHoveredElement = event.target as Element | null;
    this.hoverStartTime = Date.now();
  }

  private trackClick(event: MouseEvent): void {
    if (this.permanentlyBlocked || !this.isAllowedPath()) return;
    const element = event.target as HTMLElement | null;
    if (!element) return;
    // Do not capture a click (coordinates, element text, id, XPath) on a
    // redacted element; that was the heatmap counterpart to the replay leak.
    if (isElementCaptureBlocked(element, this.blockCaptureSelector)) return;
    const hoverDuration = this.hoverStartTime
      ? Date.now() - this.hoverStartTime
      : null;

    if (element.tagName === "A") {
      this.navigationTriggered = true;
    } else {
      this.navigationTriggered = false;
    }

    // Push click breadcrumb
    const tag = element.tagName.toLowerCase();
    const idPart = element.id ? `#${element.id}` : "";
    const textPart = element.textContent
      ? element.textContent.trim().slice(0, 30)
      : "";
    const label = textPart
      ? `${tag}${idPart} "${textPart}"`
      : `${tag}${idPart}`;
    this.pushBreadcrumb({
      type: "click",
      category: "ui.click",
      message: `Clicked ${label}`,
      timestamp: Date.now(),
    });

    const fp = this.getElementFingerprint(element as Element);

    this.queueEvent({
      type: "click",
      x: event.clientX,
      y: event.clientY,
      scrollDepth: this.getCurrentScrollDepth(),
      scrollCompletion: this.getScrollCompletion(),
      pageHeight: this.getPageHeight(), // Include total page height
      pagePath: this.getPagePath(),
      timestamp: Date.now(),
      elementTag: element.tagName.toLowerCase(),
      elementId: element.id || null,
      elementClass: element.className || null,
      elementText: element.textContent
        ? element.textContent.trim().substring(0, 100)
        : null,
      elementXPath: this.getXPath(element as Element),
      lastHoveredElement: this.lastHoveredElement
        ? this.getXPath(this.lastHoveredElement)
        : null,
      hoverDuration: hoverDuration,
      elementFingerprint: fp.elementFingerprint,
      elementSelector: fp.elementSelector,
      elementDataJv: fp.elementDataJv,
      elementAriaLabel: fp.elementAriaLabel,
      elementRole: fp.elementRole,
      elementHref: fp.elementHref,
      elementType: fp.elementType,
      elementName: fp.elementName,
      elementNthOfType: fp.elementNthOfType,
      elementAncestorPath: fp.elementAncestorPath,
    });

    this.lastHoveredElement = null;
    this.hoverStartTime = null;
  }

  private trackScroll(): void {
    if (this.permanentlyBlocked || !this.isAllowedPath()) return;
    this.queueEvent({
      type: "scroll",
      depth: this.getCurrentScrollDepth(),
      scrollCompletion: this.getScrollCompletion(),
      pagePath: this.getPagePath(),
      timestamp: Date.now(),
    });
  }

  private trackMouseMove(event: MouseEvent): void {
    if (this.permanentlyBlocked || !this.isAllowedPath()) return;
    if (
      isElementCaptureBlocked(
        event.target as Element | null,
        this.blockCaptureSelector
      )
    )
      return;
    this.queueEvent({
      type: "mousemove",
      x: event.clientX,
      y: event.clientY,
      scrollDepth: this.getCurrentScrollDepth(),
      pageHeight: this.getPageHeight(),
      pagePath: this.getPagePath(),
      timestamp: Date.now(),
    });
  }

  /**
   * Finish the current session and obtain a fresh one.
   *
   * Ordering matters: any events buffered while the old session was live are
   * sent WITH the finish beacon under the OLD session ID. They used to be
   * left in the queues and shipped by the caller under the NEW ID, which
   * planted old-timestamp events into the new session and corrupted its
   * startTime/duration.
   *
   * @param oldSessionPayload event payload (recordingEvents/interactions/...)
   *        that belongs to the session being finished.
   * @returns true when the old session's finish beacon (with its events) was
   *          dispatched and the rotation completed; false when the payload is
   *          still too large for keepalive even after shedding recordingEvents,
   *          in which case nothing was rotated and the caller should retry later.
   */
  private async sessionRestart(
    oldSessionPayload?: Partial<SendDataPayload>
  ): Promise<boolean> {
    // Rotations are a cross-tab critical section: two tabs detecting an
    // expired session at the same time each finished it and minted their own
    // replacement — one visitor, two new sessions. The lock winner rotates;
    // waiters ship their buffered events under the OLD ID (where they belong)
    // and adopt the winner's new session instead of minting another.
    return withSessionLock(async () => {
      const finishedAt = Date.now();

      const oldSessionId = this.sessionID;
      if (this.adoptStoredSessionIfAlive()) {
        if (oldSessionId) {
          const payload: Partial<SendDataPayload> = {
            apiKey: this.apiKey,
            ...(oldSessionPayload ?? {}),
            finishedSession: finishedAt,
            sessionID: oldSessionId,
            sentAt: Date.now(),
          };
          if (!this.beaconFetch(JSON.stringify(payload))) {
            this.sessionID = oldSessionId;
            return false;
          }
        }
        this.sessionEndSent = false;
        if (this.tracingModule) this.tracingModule.setSessionId(this.sessionID);
        if (this.stopfn) this.stopfn();
        this.stopfn = this.startRecording();
        return true;
      }

      if (this.sessionID) {
        const payload: Partial<SendDataPayload> = {
          apiKey: this.apiKey,
          ...(oldSessionPayload ?? {}),
          finishedSession: finishedAt,
          sessionID: this.sessionID,
          sentAt: Date.now(),
        };
        const sent = this.beaconFetch(JSON.stringify(payload));
        if (!sent) return false;
      }
      // Keep offering the retiring session ID to the handshake. Nulling it
      // here forced sessionID: null and bypassed #222's reuse-vs-rotate
      // verdict — the orphan case this PR exists to close. The server stamps
      // endTime (via the finish beacon) and returns a fresh ID with
      // sessionVerdict: "rotated". Client-IP latch reset happens in
      // setSessionVariables only when the verdict/ID actually changes.
      this.sessionEndSent = false;
      // Stamp both liveness signals NOW and synchronously: the renewal check in
      // sendData reads localStorage directly, so leaving a stale last_send
      // visible would rotate again on the very next flush (session churn).
      // setSessionVariables also stamps after mint; this covers the window
      // before the handshake returns.
      this.stampSessionLivenessNow(finishedAt);

      // Handshake with the old ID still set; server verdict rotates it.
      await this.setSessionVariables();

      // Update tracing module with new session ID
      if (this.tracingModule) this.tracingModule.setSessionId(this.sessionID);

      // Start new recording
      if (this.stopfn) this.stopfn();
      this.stopfn = this.startRecording();
      return true;
    });
  }

  private async sendData(): Promise<void> {
    this.log("debug", "Sending data at", Date.now());

    if (this.sendingData || !this.initialized || !this.sessionID) return;

    this.sendingData = true;

    // Swap queues into locals so events arriving during fetch aren't lost
    const sentEventQueue = this.eventQueue;
    const sentRecordingEvents = this.recordingEvents;
    const sentSnapshot = this.pendingDomSnapshot;

    try {
      if (
        this.eventQueue.length + this.recordingEvents.length === 0 ||
        this.shouldThrottle()
      ) {
        return;
      }

      // Refresh metadata for SPA navigations (pagePath, pageHeight, etc.)
      this.metadata = this.collectMetadata();

      if (this.debug) {
        this.sendCounter++;
        this.log("debug", `Sending batch #${this.sendCounter}`);
      }

      this.eventQueue = [];
      this.recordingEvents = [];
      this.pendingDomSnapshot = null;

      const onAllowedPath = this.isAllowedPath();
      const payload: Partial<SendDataPayload> = {
        apiKey: this.apiKey,
        // Backstop: replay events and the DOM snapshot ride the recording path
        // scope. The buffer is already discarded when crossing into an excluded
        // path (handleRecordingPathChange), so this only guards the residual
        // window; it never drops a snapshot captured legitimately on an allowed
        // page, since captureDomSnapshot refuses to capture on excluded paths.
        recordingEvents: effectiveRecordable(
          this.recordingEnabled,
          this.recordingPathAllowed,
          this.appRecordable
        )
          ? sentRecordingEvents
          : [],
        // Interactions are already path-validated at capture time (trackClick,
        // trackScroll, trackMouseMove each check isAllowedPath). Gating again
        // here at send time caused entire batches to be dropped when the user
        // navigated away from the allowed page before the flush fired.
        interactions: this.heatmapEnabled
          ? sentEventQueue.filter(
              (event) =>
                !["error", "unhandledrejection", "console"].includes(event.type)
            )
          : [],
        consoleErrors: onAllowedPath
          ? sentEventQueue.filter(
              (event) =>
                event.type === "error" || event.type === "unhandledrejection"
            )
          : [],
        metadata: this.metadata,
        generatedSessionID: this.generatedSessionID,
        domSnapshot: canCaptureDom(this.recordingPathAllowed, this.appRecordable)
          ? sentSnapshot || undefined
          : undefined,
        sdkVersion: JstVerifySDK.SDK_VERSION,
      };

      const now = Date.now();
      const storedActivity = localStorage.getItem("jstverify_last_activity");
      const lastActivity = storedActivity
        ? parseInt(storedActivity, 10)
        : this.lastActivity;

      const lastSend = localStorage.getItem("jstverify_last_send");

      this.log("debug", "Payload state:", {
        domainStatus: this.domainStatus,
        sessionID: this.sessionID,
        visitorTracking: this.visitorTracking,
        errorTracking: this.errorTracking,
        clientIPSent: this.clientIPSent,
        errorCount: (payload.consoleErrors as any[])?.length ?? 0,
        interactionCount: (payload.interactions as any[])?.length ?? 0,
        sampleRate: this.visitorSampleRate,
      });

      // Check if visitor tracking is enabled and we haven't sent the client IP yet
      if (this.visitorTracking && !this.clientIPSent) {
        this.log("debug", "Sending Client IP");
        payload.sendClientIP = true;

        // Mark that we've sent the client IP for this session
        this.writeStorage("jstverify_client_ip_sent", "true");
        this.clientIPSent = true;
      }

      // Early return when there's no meaningful data to send.
      // This prevents the idle renewal loop: no data → skip API call → no new session.
      const hasData =
        (payload.recordingEvents as any[]).length > 0 ||
        (payload.interactions as any[]).length > 0 ||
        (payload.consoleErrors as any[])?.length > 0 ||
        payload.domSnapshot ||
        payload.sendClientIP;

      if (!hasData) {
        return;
      }

      this.log("debug", "Lats send:", lastSend, "Last activity:", lastActivity);

      // Same aliveness predicate as the init restore path, so the renewal
      // check and the restore check can never disagree about what "expired"
      // means. An active user (fresh lastActivity) with a long gap between
      // sends must NOT be rotated.
      const lastSendTime = lastSend ? parseInt(lastSend, 10) : 0;
      if (!isSessionAlive(lastActivity, lastSendTime, this.sessionTimeout, now)) {
        // The buffered events belong to the session being finished: ship them
        // WITH the finish beacon under the old ID, then start clean. Sending
        // them under the new ID corrupted the new session's startTime.
        const rotated = await this.sessionRestart(payload);
        if (rotated) {
          this.recordSuccess();
          return;
        }
        // Beacon failed — keep the old session for now and retry the
        // rotation on a later flush; the events still go out below.
      }

      // Always include the session ID in subsequent requests
      payload.sessionID = this.sessionID;
      payload.sentAt = Date.now();

      const response = await fetch(this.endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.apiKey,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        this.generatedSessionID = false;
        const responseBody = await response.json();
        // Adopt the server's session ID when we have none yet, or when the
        // server explicitly rebound the session (sessionRebound: true — it
        // could not verify our ID's property binding and minted a fresh one).
        // Without the rebound case, the SDK kept resending the stale ID and
        // the server minted another new session for every batch.
        if (
          shouldAdoptServerSessionId(
            this.sessionID,
            responseBody.sessionID,
            responseBody.sessionRebound === true
          )
        ) {
          this.sessionID = responseBody.sessionID;
          if (this.sessionID) {
            this.writeStorageNow("jstverify_session_id", this.sessionID);
            if (this.tracingModule) {
              this.tracingModule.setSessionId(this.sessionID);
            }
          }
        }
        // Update toggles if the server disabled them mid-session
        if (responseBody.heatmapEnabled === false) this.heatmapEnabled = false;
        if (responseBody.recordingEnabled === false)
          this.recordingEnabled = false;
        if (responseBody.visitorTracking === false)
          this.visitorTracking = false;

        this.writeStorage("jstverify_last_send", now.toString());
        this.recordSuccess();
      } else {
        this.log(
          "error",
          "Failed to send data:",
          response.status,
          response.statusText
        );
        // Restore unsent events so they can be retried
        this.eventQueue = sentEventQueue.concat(this.eventQueue);
        this.recordingEvents = sentRecordingEvents.concat(this.recordingEvents);
        if (sentSnapshot && !this.pendingDomSnapshot) {
          this.pendingDomSnapshot = sentSnapshot;
        }
        if (response.status === 403) {
          this.recordFailure(true);
        } else {
          this.recordFailure(false);
        }
      }
    } catch (err) {
      this.log("error", "Error sending data:", err);
      try {
        this.eventQueue = sentEventQueue.concat(this.eventQueue);
        this.recordingEvents = sentRecordingEvents.concat(this.recordingEvents);
        if (sentSnapshot && !this.pendingDomSnapshot) {
          this.pendingDomSnapshot = sentSnapshot;
        }
      } catch {
        // Variables not yet assigned (early throw) — nothing to restore
      }
      this.recordFailure(false);
    } finally {
      this.sendingData = false;
    }
  }

  private queueEvent(event: EventPayload): void {
    const now = Date.now();
    this.lastActivity = now;
    this.writeStorage("jstverify_last_activity", this.lastActivity.toString());

    if (this.eventQueue.length >= JstVerifySDK.MAX_EVENT_QUEUE_SIZE) {
      this.eventQueue.shift();
    }
    event.timeSinceLastEvent = this.lastEventTimestamp
      ? now - this.lastEventTimestamp
      : 0; // First event has a delay of 0ms

    this.lastEventTimestamp = now; // Update last event timestamp

    this.eventQueue.push(event);

    if (this.eventQueue.length >= 10) {
      this.sendData().catch(() => {});
    }
  }

  private async validateApiAndDomain(): Promise<{
    valid: boolean;
    domainStatus?: string;
    allowedPaths?: string[];
    disallowedPaths?: string[];
    recordingAllowedPaths?: string[];
    recordingDisallowedPaths?: string[];
    sessionID?: string;
    visitorTracking?: boolean;
    errorTracking?: boolean;
    debugEnabled?: boolean;
    heatmapEnabled?: boolean;
    recordingEnabled?: boolean;
    tracingEnabled?: boolean;
    sampleRate?: number;
    generatedSessionID?: boolean;
    sessionVerdict?: "reused" | "rotated" | "new";
    privacyConfig?: PrivacyConfig;
  }> {
    try {
      const sessionBody: Record<string, any> = {
        apiKey: this.apiKey,
        sessionCheck: true,
        sessionID: this.sessionID,
        // Server-authoritative reuse-vs-rotate: report what we know and let
        // the server decide against the metadata row's lastInteractionEpoch.
        // lastActivity can only EXTEND the server's view of last-seen; the
        // timeout is clamped server-side to 1-30 min.
        lastActivity: this.lastActivity || undefined,
        sessionTimeout: this.sessionTimeout,
      };
      if (this.isFunctionalTest) {
        sessionBody.visitorSource = "functional_test";
        if (this.functionalTestRunId)
          sessionBody.testRunId = this.functionalTestRunId;
      }
      // Bound the handshake so a hung fetch cannot hold withSessionLock (and
      // every other tab's init/rotation) forever. Matches SESSION_LOCK_TIMEOUT_MS.
      const handshakeController = new AbortController();
      const handshakeTimer = setTimeout(
        () => handshakeController.abort(),
        SESSION_LOCK_TIMEOUT_MS
      );
      let response: Response;
      try {
        response = await fetch(this.endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": this.apiKey,
          },
          body: JSON.stringify(sessionBody),
          signal: handshakeController.signal,
        });
      } finally {
        clearTimeout(handshakeTimer);
      }

      if (!response.ok) {
        this.log(
          "error",
          "Error validating API key and domain.",
          response.status
        );
        if (response.status === 403) {
          this.recordFailure(true);
        } else {
          this.recordFailure(false);
        }
        return { valid: false };
      }

      const responseBody = await response.json();
      const domainStatus =
        typeof responseBody.domainStatus === "string"
          ? responseBody.domainStatus
          : undefined;
      const allowedPaths = Array.isArray(responseBody.allowedPaths)
        ? responseBody.allowedPaths
        : [];
      const disallowedPaths = Array.isArray(responseBody.disallowedPaths)
        ? responseBody.disallowedPaths
        : [];
      const recordingAllowedPaths = Array.isArray(
        responseBody.recordingAllowedPaths
      )
        ? responseBody.recordingAllowedPaths
        : [];
      const recordingDisallowedPaths = Array.isArray(
        responseBody.recordingDisallowedPaths
      )
        ? responseBody.recordingDisallowedPaths
        : [];
      const sessionID =
        typeof responseBody.sessionID === "string"
          ? responseBody.sessionID
          : undefined;
      const visitorTracking = responseBody.visitorTracking === true;
      const errorTracking = responseBody.errorTracking === true;
      const debugEnabled = responseBody.debugEnabled === true;
      const heatmapEnabled = responseBody.heatmapEnabled === true;
      const recordingEnabled = responseBody.recordingEnabled === true;
      const tracingEnabled = responseBody.tracingEnabled === true;
      const sampleRate =
        typeof responseBody.sampleRate === "number"
          ? responseBody.sampleRate
          : 1.0;
      const generatedSessionID = responseBody.generatedSessionID === true;
      const sessionVerdict = parseSessionVerdict(responseBody.sessionVerdict);
      const privacyConfig: PrivacyConfig =
        typeof responseBody.privacyConfig === "object" &&
        responseBody.privacyConfig !== null
          ? responseBody.privacyConfig
          : {};

      if (domainStatus !== undefined) {
        this.writeStorage("jstverify_domain_status", domainStatus);
      }
      this.writeStorage(
        "jstverify_allowed_paths",
        JSON.stringify(allowedPaths)
      );
      this.writeStorage(
        "jstverify_disallowed_paths",
        JSON.stringify(disallowedPaths)
      );
      this.writeStorage(
        "jstverify_visitor_tracking",
        visitorTracking.toString()
      );
      this.writeStorage("jstverify_error_tracking", errorTracking.toString());

      return {
        valid: domainStatus === "active",
        domainStatus,
        allowedPaths,
        disallowedPaths,
        recordingAllowedPaths,
        recordingDisallowedPaths,
        sessionID,
        visitorTracking,
        errorTracking,
        debugEnabled,
        heatmapEnabled,
        recordingEnabled,
        tracingEnabled,
        sampleRate,
        generatedSessionID,
        sessionVerdict,
        privacyConfig,
      };
    } catch (err) {
      this.log("error", "Error validating domain:", err);
      this.recordFailure(false);
      return { valid: false };
    }
  }

  public async trace<T>(name: string, fn: () => T | Promise<T>): Promise<T> {
    if (!this.tracingModule) {
      return fn();
    }
    return this.tracingModule.trace(name, fn);
  }

  private djb2Hash(str: string): string {
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) + hash + str.charCodeAt(i)) & 0xffffffff;
    }
    return (hash >>> 0).toString(36);
  }

  private isStableId(id: string): boolean {
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(id)) return false;
    if (/^[0-9a-f]{16,}$/i.test(id)) return false;
    if (/^\d+$/.test(id)) return false;
    if (/^(:r[0-9a-z]+:|react-|rc-|mui-|radix-|headlessui-)/.test(id))
      return false;
    if (/--[a-zA-Z0-9]{5,}/.test(id)) return false;
    return true;
  }

  private getAncestorPath(element: Element, depth: number): string {
    const parts: string[] = [];
    let cur: Element | null = element;
    for (let i = 0; i < depth && cur; i++) {
      cur = cur.parentElement;
      if (!cur) break;
      const tag = cur.tagName.toLowerCase();
      const firstClass =
        cur.classList && cur.classList.length > 0 ? `.${cur.classList[0]}` : "";
      parts.unshift(`${tag}${firstClass}`);
    }
    parts.push(element.tagName.toLowerCase());
    return parts.join(" > ");
  }

  private getCssSelector(element: Element): string {
    const jvAttr = element.getAttribute("data-jv-element");
    if (jvAttr) return `[data-jv-element="${jvAttr}"]`;

    const tag = element.tagName.toLowerCase();

    if (element.id && this.isStableId(element.id)) {
      return `${tag}#${element.id}`;
    }

    const ariaLabel = element.getAttribute("aria-label");
    if (ariaLabel) return `${tag}[aria-label="${ariaLabel}"]`;

    let nthOfType = 1;
    let sibling = element.previousElementSibling;
    while (sibling) {
      if (sibling.tagName === element.tagName) nthOfType += 1;
      sibling = sibling.previousElementSibling;
    }

    const stableClass = Array.from(element.classList || []).find(
      (cls) => !/[0-9a-f]{6,}/i.test(cls) && !/--[a-zA-Z0-9]{5,}/.test(cls)
    );

    const parentTag = element.parentElement
      ? element.parentElement.tagName.toLowerCase()
      : "";
    const parentPrefix = parentTag ? `${parentTag} > ` : "";
    const classPart = stableClass ? `.${stableClass}` : "";

    return `${parentPrefix}${tag}${classPart}:nth-of-type(${nthOfType})`;
  }

  private getElementFingerprint(element: Element): {
    elementFingerprint: string;
    elementSelector: string;
    elementDataJv: string | null;
    elementAriaLabel: string | null;
    elementRole: string | null;
    elementHref: string | null;
    elementType: string | null;
    elementName: string | null;
    elementNthOfType: number;
    elementAncestorPath: string;
  } {
    const tag = element.tagName.toLowerCase();
    const dataJv = element.getAttribute("data-jv-element");
    const ariaLabel = element.getAttribute("aria-label");
    const role = element.getAttribute("role");
    const href = element.getAttribute("href");
    const type = element.getAttribute("type");
    const name = element.getAttribute("name");

    let nthOfType = 1;
    let sibling = element.previousElementSibling;
    while (sibling) {
      if (sibling.tagName === element.tagName) nthOfType += 1;
      sibling = sibling.previousElementSibling;
    }

    const ancestorPath = this.getAncestorPath(element, 2);
    const selector = this.getCssSelector(element);

    let fingerprint: string;

    if (dataJv) {
      fingerprint = `jv:${dataJv}`;
    } else if (element.id && this.isStableId(element.id)) {
      fingerprint = `id:${element.id}`;
    } else {
      const stableAttrs = [ariaLabel, role, name, type]
        .filter(Boolean)
        .join("|");
      const textContent = (element.textContent || "").trim().slice(0, 50);
      const textHash = textContent ? this.djb2Hash(textContent) : "";
      const composite = `${tag}|${stableAttrs}|${textHash}|${nthOfType}|${ancestorPath}`;
      fingerprint = this.djb2Hash(composite);
    }

    return {
      elementFingerprint: fingerprint,
      elementSelector: selector,
      elementDataJv: dataJv,
      elementAriaLabel: ariaLabel,
      elementRole: role,
      elementHref: href,
      elementType: type,
      elementName: name,
      elementNthOfType: nthOfType,
      elementAncestorPath: ancestorPath,
    };
  }

  private getXPath(element: Element): string | null {
    try {
      if (!element) return null;
      // Walk up from text/comment nodes to the nearest Element
      while (element && element.nodeType !== 1) {
        element = element.parentNode as Element;
      }
      if (!element) return null;
      // Short-circuit: if this element (or a close ancestor) has a stable id, use it
      let cur: Element | null = element;
      for (let depth = 0; cur && depth < 3; depth += 1) {
        if (cur.nodeType === 1 && cur.id && this.isStableId(cur.id)) {
          const safeId = cur.id.replace(/["\\]/g, "");
          return `//*[@id="${safeId}"]`;
        }
        cur = cur.parentElement;
      }
      // Build a positional path
      let path: string[] = [];
      let node: Element | null = element;
      while (node?.nodeType === 1) {
        let index = 1;
        let sibling = node.previousSibling;
        while (sibling) {
          if (
            sibling.nodeType === 1 &&
            (sibling as Element).tagName === node.tagName
          ) {
            index += 1;
          }
          sibling = sibling.previousSibling;
        }
        path.unshift(`${node.tagName}[${index}]`);
        node = node.parentNode as Element;
      }
      return "/" + path.join("/");
    } catch {
      return null;
    }
  }
  private pushBreadcrumb(bc: Breadcrumb): void {
    if (this.breadcrumbs.length >= JstVerifySDK.MAX_BREADCRUMBS) {
      this.breadcrumbs.shift();
    }
    this.breadcrumbs.push(bc);
  }

  private onSpaNavigation(): void {
    // Recording-boundary handling must run on EVERY SPA navigation, even with
    // heatmaps off, since recording can be enabled independently. It stops and
    // discards on entering an excluded path and restarts on leaving one.
    this.handleRecordingPathChange();

    if (!this.heatmapEnabled) return;
    if (this.spaNavigationTimer) clearTimeout(this.spaNavigationTimer);
    this.spaNavigationTimer = setTimeout(() => {
      this.spaNavigationTimer = null;
      this.captureDomSnapshot("content-change");
    }, 300);
  }

  private setupBreadcrumbTracking(): void {
    this.breadcrumbs = [];
    this.lastNavigationUrl = window.location.href;

    // Navigation breadcrumbs: monkey-patch pushState/replaceState
    const self = this;
    this.originalPushState = history.pushState;
    this.originalReplaceState = history.replaceState;

    history.pushState = function (
      ...args: Parameters<typeof history.pushState>
    ) {
      const from = self.lastNavigationUrl;
      self.originalPushState!.apply(this, args);
      const to = window.location.href;
      self.lastNavigationUrl = to;
      self.pushBreadcrumb({
        type: "navigation",
        category: "navigation",
        message: `Navigated to ${window.location.pathname}`,
        timestamp: Date.now(),
        data: JSON.stringify({ from, to }),
      });
      self.onSpaNavigation();
    };

    history.replaceState = function (
      ...args: Parameters<typeof history.replaceState>
    ) {
      const from = self.lastNavigationUrl;
      self.originalReplaceState!.apply(this, args);
      const to = window.location.href;
      self.lastNavigationUrl = to;
      self.pushBreadcrumb({
        type: "navigation",
        category: "navigation",
        message: `Replaced to ${window.location.pathname}`,
        timestamp: Date.now(),
        data: JSON.stringify({ from, to }),
      });
      self.onSpaNavigation();
    };

    this.boundPopStateHandler = () => {
      const from = self.lastNavigationUrl;
      const to = window.location.href;
      self.lastNavigationUrl = to;
      self.pushBreadcrumb({
        type: "navigation",
        category: "navigation",
        message: `Navigated to ${window.location.pathname}`,
        timestamp: Date.now(),
        data: JSON.stringify({ from, to }),
      });
      self.onSpaNavigation();
    };
    window.addEventListener("popstate", this.boundPopStateHandler);

    // Console breadcrumbs: intercept console.error and console.warn
    this.originalConsoleError = console.error;
    this.originalConsoleWarn = console.warn;

    console.error = (...args: any[]) => {
      // Avoid capturing our own SDK log messages
      const first = args[0];
      if (typeof first === "string" && first.startsWith("[JstVerify:")) {
        this.originalConsoleError!.apply(console, args);
        return;
      }
      const message = args
        .map((a) => {
          try {
            return typeof a === "string" ? a : JSON.stringify(a);
          } catch {
            return String(a);
          }
        })
        .join(" ")
        .slice(0, 200);
      this.pushBreadcrumb({
        type: "console",
        category: "console.error",
        message,
        timestamp: Date.now(),
      });
      this.originalConsoleError!.apply(console, args);
    };

    console.warn = (...args: any[]) => {
      const first = args[0];
      if (typeof first === "string" && first.startsWith("[JstVerify:")) {
        this.originalConsoleWarn!.apply(console, args);
        return;
      }
      const message = args
        .map((a) => {
          try {
            return typeof a === "string" ? a : JSON.stringify(a);
          } catch {
            return String(a);
          }
        })
        .join(" ")
        .slice(0, 200);
      this.pushBreadcrumb({
        type: "console",
        category: "console.warn",
        message,
        timestamp: Date.now(),
      });
      this.originalConsoleWarn!.apply(console, args);
    };

    // Network breadcrumbs: wrap fetch to capture failed requests.
    // Skip when tracing is active (server-enabled or client-requested) — the
    // TracingModule already instruments fetch and captures all request data.
    if (
      !this.tracingEnabled &&
      !this.tracingModule &&
      typeof window !== "undefined" &&
      window.fetch
    ) {
      const originalFetch = window.fetch;
      this.originalFetch = originalFetch;
      const sdk = this;
      window.fetch = function (
        input: RequestInfo | URL,
        init?: RequestInit
      ): Promise<Response> {
        const method = init?.method || "GET";
        const url =
          typeof input === "string"
            ? input
            : input instanceof URL
            ? input.href
            : (input as Request).url;
        return originalFetch.apply(this, [input, init] as any).then(
          (response: Response) => {
            if (response.status >= 400) {
              sdk.pushBreadcrumb({
                type: "network",
                category: "fetch",
                message: `${method} ${url} [${response.status}]`,
                timestamp: Date.now(),
                data: JSON.stringify({ method, url, status: response.status }),
              });
            }
            return response;
          },
          (error: any) => {
            sdk.pushBreadcrumb({
              type: "network",
              category: "fetch",
              message: `${method} ${url} [Network Error]`,
              timestamp: Date.now(),
              data: JSON.stringify({
                method,
                url,
                error: String(error).slice(0, 200),
              }),
            });
            throw error;
          }
        );
      };
    }
  }

  private teardownBreadcrumbTracking(): void {
    // Restore console originals
    if (this.originalConsoleError) {
      console.error = this.originalConsoleError;
      this.originalConsoleError = null;
    }
    if (this.originalConsoleWarn) {
      console.warn = this.originalConsoleWarn;
      this.originalConsoleWarn = null;
    }
    // Restore history methods
    if (this.originalPushState) {
      history.pushState = this.originalPushState;
      this.originalPushState = null;
    }
    if (this.originalReplaceState) {
      history.replaceState = this.originalReplaceState;
      this.originalReplaceState = null;
    }
    if (this.boundPopStateHandler) {
      window.removeEventListener("popstate", this.boundPopStateHandler);
      this.boundPopStateHandler = null;
    }
    // Restore fetch
    if (this.originalFetch) {
      window.fetch = this.originalFetch;
      this.originalFetch = null;
    }
    this.breadcrumbs = [];
  }

  public destroy(): void {
    // Flush pending data BEFORE stopping recording to avoid losing events
    this.flushStorage();
    this.flush();

    if (this.stopfn) {
      this.stopfn();
      this.stopfn = undefined;
    }
    if (this.flushIntervalId) {
      clearTimeout(this.flushIntervalId);
      this.flushIntervalId = null;
    }
    if (this.boundClickHandler) {
      window.removeEventListener("click", this.boundClickHandler);
      this.boundClickHandler = null;
    }
    if (this.boundScrollHandler) {
      window.removeEventListener("scroll", this.boundScrollHandler);
      this.boundScrollHandler = null;
    }
    if (this.boundMouseMoveHandler) {
      window.removeEventListener("mousemove", this.boundMouseMoveHandler);
      this.boundMouseMoveHandler = null;
    }
    if (this.boundMouseOverHandler) {
      document.removeEventListener("mouseover", this.boundMouseOverHandler);
      this.boundMouseOverHandler = null;
    }
    if (this.boundErrorHandler) {
      window.removeEventListener(
        "error",
        this.boundErrorHandler as EventListener,
        true
      );
      this.boundErrorHandler = null;
    }
    if (this.boundRejectionHandler) {
      window.removeEventListener(
        "unhandledrejection",
        this.boundRejectionHandler as EventListener
      );
      this.boundRejectionHandler = null;
    }
    if (this.boundVisibilityHandler) {
      document.removeEventListener(
        "visibilitychange",
        this.boundVisibilityHandler
      );
      this.boundVisibilityHandler = null;
    }
    if (this.boundPageHideHandler) {
      window.removeEventListener("pagehide", this.boundPageHideHandler);
      this.boundPageHideHandler = null;
    }
    if (this.boundBeforeUnloadHandler) {
      window.removeEventListener("beforeunload", this.boundBeforeUnloadHandler);
      this.boundBeforeUnloadHandler = null;
    }
    if (this.domObserver) {
      this.domObserver.disconnect();
      this.domObserver = null;
    }
    if (this.domObserverTimer) {
      clearTimeout(this.domObserverTimer);
      this.domObserverTimer = null;
    }
    if (this.spaNavigationTimer) {
      clearTimeout(this.spaNavigationTimer);
      this.spaNavigationTimer = null;
    }
    if (this.tabHeartbeatId) {
      clearInterval(this.tabHeartbeatId);
      this.tabHeartbeatId = null;
    }
    this.tabRegistry?.unregister();
    this.tabRegistry = null;
    this.lastContentHashMap.clear();
    if (this.tracingModule) {
      this.tracingModule.stop();
      this.tracingModule = null;
    }
    this.teardownBreadcrumbTracking();
    this.isFunctionalTest = false;
    this.functionalTestRunId = null;
    // Reset the app pause on full teardown so a later init() starts fresh. (The
    // init state reset deliberately leaves appRecordable alone so a pause set
    // before init is still honored at the handshake.)
    this.appRecordable = true;
    this.initialized = false;
  }

  private mergePrivacyConfigs(
    server: PrivacyConfig,
    client: PrivacyConfig
  ): PrivacyConfig {
    return {
      maskAllInputs: !!(server.maskAllInputs || client.maskAllInputs),
      maskInputOptions: {
        // Password masks by DEFAULT (matches rrweb's own default) unless a side
        // explicitly opts out. The old `!!(server || client)` yielded an
        // explicit `false` when neither side set it, and because the merged
        // object is always defined, rrweb's `{ password: true }` fallback never
        // ran, leaking password inputs for any property with no privacy config.
        password: resolvePasswordMask(
          server.maskInputOptions?.password,
          client.maskInputOptions?.password
        ),
        email: !!(
          server.maskInputOptions?.email || client.maskInputOptions?.email
        ),
        tel: !!(server.maskInputOptions?.tel || client.maskInputOptions?.tel),
        text: !!(
          server.maskInputOptions?.text || client.maskInputOptions?.text
        ),
        number: !!(
          server.maskInputOptions?.number || client.maskInputOptions?.number
        ),
        search: !!(
          server.maskInputOptions?.search || client.maskInputOptions?.search
        ),
      },
      maskTextSelector:
        [server.maskTextSelector, client.maskTextSelector]
          .filter(Boolean)
          .join(", ") || undefined,
      blockSelector:
        [server.blockSelector, client.blockSelector]
          .filter(Boolean)
          .join(", ") || undefined,
      maskTextClass: server.maskTextClass || client.maskTextClass || undefined,
      blockClass: server.blockClass || client.blockClass || undefined,
    };
  }

  private buildEventPayload(): Pick<
    SendDataPayload,
    | "recordingEvents"
    | "interactions"
    | "consoleErrors"
    | "metadata"
    | "domSnapshot"
  > {
    const onAllowedPath = this.isAllowedPath();
    return {
      recordingEvents: effectiveRecordable(
        this.recordingEnabled,
        this.recordingPathAllowed,
        this.appRecordable
      )
        ? this.recordingEvents
        : [],
      interactions:
        this.heatmapEnabled && onAllowedPath
          ? this.eventQueue.filter(
              (event) =>
                !["error", "unhandledrejection", "console"].includes(event.type)
            )
          : [],
      consoleErrors: onAllowedPath
        ? this.eventQueue.filter(
            (event) =>
              event.type === "error" || event.type === "unhandledrejection"
          )
        : [],
      metadata: this.metadata,
      domSnapshot: canCaptureDom(this.recordingPathAllowed, this.appRecordable)
        ? this.pendingDomSnapshot || undefined
        : undefined,
    };
  }

  public flush(): void {
    if (this.sendingData) return;
    if (this.shouldThrottle()) return;
    this.flushStorage();
    // No session yet (handshake still in flight): a beacon with
    // sessionID: null makes the server mint a fresh session for the batch,
    // orphaning it from the visitor's real session. Keep the events queued —
    // sendData() will deliver them once the handshake assigns an ID.
    if (!this.sessionID) return;
    if (
      this.eventQueue.length + this.recordingEvents.length === 0 &&
      !this.pendingDomSnapshot
    )
      return;
    const eventPayload = this.buildEventPayload();
    const payload: Partial<SendDataPayload> = {
      apiKey: this.apiKey,
      ...eventPayload,
      sessionID: this.sessionID,
      sentAt: Date.now(),
    };
    const sent = this.beaconFetch(JSON.stringify(payload));
    if (!sent) {
      this.log(
        "warn",
        "beaconFetch failed — data will be retried on next sendData() cycle"
      );
    }
    if (this.tracingModule) {
      this.tracingModule.flushBeacon();
    }
    if (sent) {
      this.eventQueue = [];
      this.recordingEvents = [];
      this.pendingDomSnapshot = null;
    }
  }

  public getSessionId(): string | null {
    return this.sessionID;
  }
}

const jstverifyInstance = new JstVerifySDK();

export function init(config: Config) {
  return jstverifyInstance.init(config);
}

export function destroy() {
  jstverifyInstance.destroy();
}

export function flush() {
  jstverifyInstance.flush();
}

export function getSessionId(): string | null {
  return jstverifyInstance.getSessionId();
}

export function trace<T>(name: string, fn: () => T | Promise<T>): Promise<T> {
  return jstverifyInstance.trace(name, fn);
}

export function pauseRecording() {
  jstverifyInstance.pauseRecording();
}

export function resumeRecording() {
  jstverifyInstance.resumeRecording();
}

export function setPageRecordable(recordable: boolean) {
  jstverifyInstance.setPageRecordable(recordable);
}
